import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { useConfig } from "@/context/ConfigContext";

export default function Contact() {
  const { config } = useConfig();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Veuillez indiquer votre nom."); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Adresse e-mail invalide."); return; }
    if (form.message.trim().length < 5) { toast.error("Votre message est trop court."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé ! Nous vous répondrons rapidement.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <PublicLayout>
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(160deg,#1C1F45 0%,#24285A 55%,#2E3370 100%)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white">Contactez-nous</h1>
          <p className="text-lg text-slate-300 mt-4 max-w-xl mx-auto">Une question sur votre couverture santé en France ou à l'étranger ? Notre courtier vous répond.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2 space-y-5">
          <h2 className="font-display text-xl font-bold text-slate-900">Nos coordonnées</h2>
          <div className="flex items-start gap-3"><span className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0"><Mail className="text-indigo-600" size={18} /></span><div><p className="text-sm text-slate-500">E-mail</p><p className="font-semibold text-slate-900">{config.contact_email || "contact@assureconseil.com"}</p></div></div>
          {config.contact_phone && <div className="flex items-start gap-3"><span className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0"><Phone className="text-indigo-600" size={18} /></span><div><p className="text-sm text-slate-500">Téléphone</p><p className="font-semibold text-slate-900">{config.contact_phone}</p></div></div>}
          {config.address && <div className="flex items-start gap-3"><span className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0"><MapPin className="text-indigo-600" size={18} /></span><div><p className="text-sm text-slate-500">Adresse</p><p className="font-semibold text-slate-900">{config.address}</p></div></div>}
          <p className="text-sm text-slate-500 leading-relaxed pt-2">Nous vous répondons sous 24 à 48h ouvrées. Vos informations ne sont utilisées que pour traiter votre demande.</p>
        </div>

        <Card className="md:col-span-3 p-7 rounded-3xl shadow-xl" data-testid="contact-form">
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="mx-auto text-indigo-600 mb-4" size={44} />
              <h3 className="font-display text-xl font-bold text-slate-900">Merci, message bien reçu !</h3>
              <p className="text-slate-600 mt-2">Un conseiller ASSURE CONSEIL vous recontactera rapidement.</p>
              <Button className="mt-6" variant="outline" onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", message: "" }); }}>Envoyer un autre message</Button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="mb-2 block font-semibold">Nom <span className="text-indigo-500">*</span></Label><Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-xl" /></div>
                <div><Label className="mb-2 block font-semibold">Téléphone</Label><Input data-testid="contact-phone-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" className="h-12 rounded-xl" /></div>
              </div>
              <div><Label className="mb-2 block font-semibold">E-mail <span className="text-indigo-500">*</span></Label><Input data-testid="contact-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@exemple.fr" className="h-12 rounded-xl" /></div>
              <div><Label className="mb-2 block font-semibold">Votre message <span className="text-indigo-500">*</span></Label><Textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Décrivez votre besoin…" className="rounded-xl" /></div>
              <Button className="btn-gradient border-0 rounded-full h-12 px-7 w-full text-base" onClick={submit} disabled={loading} data-testid="contact-submit">
                {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}Envoyer mon message
              </Button>
            </div>
          )}
        </Card>
      </div>
    </PublicLayout>
  );
}
